import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Table,
  Loader,
  Segment,
  Header,
  Label,
  Grid,
  Icon,
  Input,
  Dropdown,
  Accordion,
  Message as SuiMessage,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

// --- Risk color helpers ---
const SEVERITY_COLORS = { S1: 'green', S2: 'yellow', S3: 'orange', S4: 'red' };
const RISK_BG = {
  UNACCEPTABLE: '#fff0f0',
  ALARP: '#fff8f0',
  ACCEPTABLE: 'transparent',
};

function riskColor(severityCode) {
  return SEVERITY_COLORS[severityCode] || 'grey';
}

function riskRowStyle(riskLevel) {
  return { backgroundColor: RISK_BG[riskLevel] || 'transparent' };
}

// --- Sub-components ---

const MessageHeader = ({ msg, risk }) => {
  const englishSource = msg.corrected_default || msg.msgid;
  return (
    <div style={{ marginBottom: '1.5em' }}>
      <Header as="h1" style={{ marginBottom: '0.3em' }}>
        {englishSource}
        {risk && (
          <Label
            color={riskColor(risk.severity_code)}
            style={{ marginLeft: '0.8em', verticalAlign: 'middle' }}
          >
            {risk.severity_code} — {risk.severity_name}
          </Label>
        )}
        <Header.Subheader style={{ marginTop: '0.4em' }}>
          Message #{msg.message_id}
          <span style={{ margin: '0 0.5em', color: '#ccc' }}>|</span>
          <Label size="small" color="blue">{msg.platform}</Label>
          {msg.domain && <Label size="small">{msg.domain}</Label>}
          {msg.msgctxt && <Label size="small">ctx: {msg.msgctxt}</Label>}
          {msg.element_type_name && (
            <Label size="small" color="teal">
              <Icon name="tag" />
              {msg.element_type_name}
            </Label>
          )}
        </Header.Subheader>
      </Header>
    </div>
  );
};

const ConceptSection = ({ concept, disambiguations, sourceEvidence }) => {
  if (!concept) return null;
  return (
    <Segment>
      <Header as="h3">
        <Icon name="lightbulb outline" />
        Terminological Concept
        {concept.subject_field && (
          <Label size="small" style={{ marginLeft: '0.5em' }}>
            {concept.subject_field}
          </Label>
        )}
      </Header>
      <blockquote
        style={{
          borderLeft: '4px solid #2185d0',
          padding: '0.8em 1em',
          margin: '0.5em 0 1em',
          fontStyle: 'italic',
          fontSize: '1.1em',
          background: '#f8f9fa',
        }}
      >
        {concept.definition}
      </blockquote>
      {concept.broader_definition && (
        <div style={{ marginBottom: '1em', color: '#666' }}>
          <strong>Broader concept:</strong> {concept.broader_definition}
          {concept.broader_subject_field && (
            <Label size="mini" style={{ marginLeft: '0.5em' }}>
              {concept.broader_subject_field}
            </Label>
          )}
        </div>
      )}
      {concept.concept_notes && (
        <div style={{ marginBottom: '1em', color: '#666' }}>
          <strong>Notes:</strong> {concept.concept_notes}
        </div>
      )}
      {disambiguations.length > 0 && (
        <div style={{ marginBottom: '1em' }}>
          <Header as="h4" style={{ marginBottom: '0.5em' }}>
            Disambiguations
          </Header>
          <Table compact celled size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Contrast Term</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Distinction</Table.HeaderCell>
                <Table.HeaderCell>Contrast Msg</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {disambiguations.map((d, i) => (
                <Table.Row key={i}>
                  <Table.Cell>
                    <strong>{d.contrast_term}</strong>
                  </Table.Cell>
                  <Table.Cell>
                    <Label size="tiny">{d.disambiguation_type}</Label>
                  </Table.Cell>
                  <Table.Cell>{d.distinction}</Table.Cell>
                  <Table.Cell>
                    {d.contrast_message_id ? (
                      <Link to={`/polyglot/messages/${d.contrast_message_id}`}>
                        #{d.contrast_message_id}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
      {sourceEvidence.length > 0 && (
        <div>
          <Header as="h4" style={{ marginBottom: '0.5em' }}>
            Source Evidence
          </Header>
          <Table compact celled size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Path</Table.HeaderCell>
                <Table.HeaderCell>Component</Table.HeaderCell>
                <Table.HeaderCell>UI Element</Table.HeaderCell>
                <Table.HeaderCell>Method</Table.HeaderCell>
                <Table.HeaderCell>Finding</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sourceEvidence.map((se, i) => (
                <Table.Row key={i}>
                  <Table.Cell>
                    <code style={{ fontSize: '0.85em' }}>
                      {se.source_path}
                      {se.source_line ? `:${se.source_line}` : ''}
                    </code>
                  </Table.Cell>
                  <Table.Cell>{se.component_name || '-'}</Table.Cell>
                  <Table.Cell>
                    {se.ui_element_type && (
                      <Label size="tiny">{se.ui_element_type}</Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {se.inspection_method && (
                      <Label size="tiny" basic>
                        {se.inspection_method}
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>{se.finding_summary || '-'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </Segment>
  );
};

const EnglishSourceSection = ({ msg, correctionDetails }) => {
  const hasCorrection =
    msg.original_default &&
    msg.corrected_default &&
    msg.original_default !== msg.corrected_default;
  const hasTimeline = correctionDetails.length > 0;

  // If no correction and no timeline, the header already shows the english source
  if (!hasCorrection && !hasTimeline && !msg.ec_notes) return null;

  return (
    <Segment>
      <Header as="h3">
        <Icon name="file text outline" />
        English Corrections
      </Header>
      {hasCorrection && (
        <Grid columns={2} stackable>
          <Grid.Column>
            <Header as="h5" color="red">
              Original
            </Header>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                background: '#fff5f5',
                padding: '0.8em',
                borderRadius: '4px',
                border: '1px solid #fcc',
                margin: 0,
              }}
            >
              {msg.original_default}
            </pre>
          </Grid.Column>
          <Grid.Column>
            <Header as="h5" color="green">
              Corrected
            </Header>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                background: '#f5fff5',
                padding: '0.8em',
                borderRadius: '4px',
                border: '1px solid #cfc',
                margin: 0,
              }}
            >
              {msg.corrected_default}
            </pre>
          </Grid.Column>
        </Grid>
      )}
      {msg.ec_notes && (
        <div style={{ marginTop: '0.5em', color: '#666', fontStyle: 'italic' }}>
          {msg.ec_notes}
        </div>
      )}
      {hasTimeline && (
        <div style={{ marginTop: '1em' }}>
          <Header as="h4" style={{ marginBottom: '0.5em' }}>
            Correction Timeline
          </Header>
          <Table compact celled size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Old</Table.HeaderCell>
                <Table.HeaderCell>New</Table.HeaderCell>
                <Table.HeaderCell>By</Table.HeaderCell>
                <Table.HeaderCell>When</Table.HeaderCell>
                <Table.HeaderCell>Notes</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {correctionDetails.map((cd, i) => (
                <Table.Row key={i}>
                  <Table.Cell>
                    <Label size="tiny">{cd.correction_type}</Label>
                  </Table.Cell>
                  <Table.Cell style={{ color: '#c00' }}>
                    {cd.old_value || '-'}
                  </Table.Cell>
                  <Table.Cell style={{ color: '#080' }}>
                    {cd.new_value || '-'}
                  </Table.Cell>
                  <Table.Cell>{cd.applied_by || '-'}</Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                    {cd.applied_at
                      ? new Date(cd.applied_at).toLocaleDateString()
                      : '-'}
                  </Table.Cell>
                  <Table.Cell>{cd.notes || '-'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </Segment>
  );
};

const SourceCodeSection = ({ sourceCode, siblingMessages, sourceRefs }) => {
  const [expandedCtx, setExpandedCtx] = useState({});

  const toggleCtx = useCallback((key) => {
    setExpandedCtx((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Fallback: if no source_code rows but legacy sourceRefs exist, show old style
  if (!sourceCode || sourceCode.length === 0) {
    if (!sourceRefs || sourceRefs.length === 0) return null;
    return (
      <Segment>
        <Header as="h3">
          <Icon name="code" />
          Source References
        </Header>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4em' }}>
          {sourceRefs.map((ref, i) => (
            <Label basic key={i} style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
              {typeof ref === 'string' ? ref : JSON.stringify(ref)}
            </Label>
          ))}
        </div>
      </Segment>
    );
  }

  // Group locations by source_file
  const byFile = {};
  sourceCode.forEach((sc) => {
    if (!byFile[sc.source_file]) byFile[sc.source_file] = [];
    byFile[sc.source_file].push(sc);
  });
  const fileEntries = Object.entries(byFile);

  return (
    <Segment>
      <Header as="h3">
        <Icon name="code" />
        Source Code Locations
        <Header.Subheader>
          {sourceCode.length} location{sourceCode.length !== 1 ? 's' : ''} across{' '}
          {fileEntries.length} file{fileEntries.length !== 1 ? 's' : ''}
        </Header.Subheader>
      </Header>

      {fileEntries.map(([filePath, locations]) => {
        const repo = locations[0].source_repo;
        return (
          <div key={filePath} style={{ marginBottom: '1.2em' }}>
            <div style={{ marginBottom: '0.4em' }}>
              <Label size="small" color="teal">{repo}</Label>
              <span style={{ fontFamily: 'monospace', fontSize: '0.9em', marginLeft: '0.5em' }}>
                {filePath}
              </span>
            </div>
            <Table compact size="small" basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={1}>Line</Table.HeaderCell>
                  <Table.HeaderCell width={3}>Component</Table.HeaderCell>
                  <Table.HeaderCell width={3}>Variable</Table.HeaderCell>
                  <Table.HeaderCell width={7}>defaultMessage</Table.HeaderCell>
                  <Table.HeaderCell width={2}>Commit</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {locations.map((loc, idx) => {
                  const ctxKey = `${filePath}:${idx}`;
                  return (
                    <React.Fragment key={ctxKey}>
                      <Table.Row>
                        <Table.Cell style={{ fontFamily: 'monospace' }}>
                          {loc.line_number || '-'}
                        </Table.Cell>
                        <Table.Cell>{loc.component_name || '-'}</Table.Cell>
                        <Table.Cell style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                          {loc.variable_name || '-'}
                        </Table.Cell>
                        <Table.Cell style={{ fontSize: '0.9em' }}>
                          {loc.default_message || '-'}
                        </Table.Cell>
                        <Table.Cell style={{ fontFamily: 'monospace', fontSize: '0.8em' }}>
                          {loc.source_commit ? loc.source_commit.slice(0, 7) : '-'}
                        </Table.Cell>
                      </Table.Row>
                      {loc.source_context && (
                        <Table.Row>
                          <Table.Cell colSpan={5} style={{ padding: 0, border: 'none' }}>
                            <Accordion>
                              <Accordion.Title
                                active={!!expandedCtx[ctxKey]}
                                onClick={() => toggleCtx(ctxKey)}
                                style={{ padding: '0.3em 0.5em', fontSize: '0.85em', color: '#888' }}
                              >
                                <Icon name="dropdown" />
                                Block context (lines {loc.block_start_line}–{loc.block_end_line})
                              </Accordion.Title>
                              <Accordion.Content active={!!expandedCtx[ctxKey]}>
                                <pre style={{
                                  background: '#f8f8f8', padding: '0.8em', margin: '0 0.5em 0.5em',
                                  fontSize: '0.8em', overflow: 'auto', maxHeight: '20em',
                                  border: '1px solid #e0e0e0', borderRadius: '4px',
                                }}>
                                  {loc.source_context}
                                </pre>
                              </Accordion.Content>
                            </Accordion>
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </React.Fragment>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        );
      })}

      {siblingMessages && siblingMessages.length > 0 && (
        <div style={{ marginTop: '1em' }}>
          <Header as="h4" style={{ marginBottom: '0.5em' }}>
            <Icon name="sitemap" />
            Sibling Messages (same source files)
          </Header>
          <Table compact size="small" basic="very">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={1}>ID</Table.HeaderCell>
                <Table.HeaderCell width={5}>English Source</Table.HeaderCell>
                <Table.HeaderCell width={4}>File</Table.HeaderCell>
                <Table.HeaderCell width={1}>Line</Table.HeaderCell>
                <Table.HeaderCell width={3}>Component</Table.HeaderCell>
                <Table.HeaderCell width={2}>Variable</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {siblingMessages.map((sib) => (
                <Table.Row key={sib.message_id}>
                  <Table.Cell>
                    <Link to={`/polyglot/messages/${sib.message_id}`}>
                      #{sib.message_id}
                    </Link>
                  </Table.Cell>
                  <Table.Cell style={{ fontSize: '0.9em' }}>{sib.english_source}</Table.Cell>
                  <Table.Cell style={{ fontFamily: 'monospace', fontSize: '0.8em' }}>
                    {sib.source_file}
                  </Table.Cell>
                  <Table.Cell style={{ fontFamily: 'monospace' }}>
                    {sib.line_number || '-'}
                  </Table.Cell>
                  <Table.Cell>{sib.component_name || '-'}</Table.Cell>
                  <Table.Cell style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                    {sib.variable_name || '-'}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </Segment>
  );
};

const CrossPlatformSection = ({ matches }) => {
  if (!matches || matches.length === 0) return null;
  return (
    <Segment>
      <Header as="h3">
        <Icon name="exchange" />
        Same Message on Other Platforms
      </Header>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em' }}>
        {matches.map((m) => (
          <Label
            key={m.id}
            as={Link}
            to={`/polyglot/messages/${m.id}`}
            style={{ cursor: 'pointer' }}
          >
            <Icon name="linkify" />
            #{m.id} — {m.platform}
            {m.domain && <Label.Detail>{m.domain}</Label.Detail>}
          </Label>
        ))}
      </div>
    </Segment>
  );
};

const RiskClassificationSection = ({ risk }) => {
  if (!risk) return null;
  return (
    <Segment>
      <Header as="h3">
        <Icon name="shield" />
        Risk Classification (ISO 14971)
      </Header>
      <Grid columns={2} stackable>
        <Grid.Column>
          <Table definition compact size="small">
            <Table.Body>
              <Table.Row>
                <Table.Cell width={5}>Severity</Table.Cell>
                <Table.Cell>
                  <Label color={riskColor(risk.severity_code)}>
                    {risk.severity_code}
                  </Label>{' '}
                  {risk.severity_name}
                  {risk.severity_desc && (
                    <span style={{ color: '#666' }}> — {risk.severity_desc}</span>
                  )}
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Category</Table.Cell>
                <Table.Cell>{risk.risk_category || '-'}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Method</Table.Cell>
                <Table.Cell>
                  <Label size="tiny" basic>
                    {risk.classification_method}
                  </Label>
                  {risk.classification_rule && (
                    <span style={{ color: '#666', marginLeft: '0.5em' }}>
                      {risk.classification_rule}
                    </span>
                  )}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Grid.Column>
        <Grid.Column>
          <Table definition compact size="small">
            <Table.Body>
              <Table.Row>
                <Table.Cell width={5}>Hazard</Table.Cell>
                <Table.Cell>{risk.hazard_description || '-'}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Classified by</Table.Cell>
                <Table.Cell>
                  {risk.classified_by || '-'}
                  {risk.classified_at && (
                    <span style={{ color: '#666', marginLeft: '0.5em' }}>
                      ({new Date(risk.classified_at).toLocaleDateString()})
                    </span>
                  )}
                </Table.Cell>
              </Table.Row>
              {risk.override_by && (
                <Table.Row>
                  <Table.Cell>Override</Table.Cell>
                  <Table.Cell>
                    {risk.override_by} — {risk.override_reason}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Grid.Column>
      </Grid>
    </Segment>
  );
};

const LanguageDetailPanel = ({ detail, standardTranslations }) => {
  if (!detail) return null;
  const { language, corrections, verifications, conventions } = detail;

  // Filter standard translations for this language
  const langStd = standardTranslations.filter(
    (st) => st.language_code === language,
  );

  return (
    <Segment color="blue" style={{ marginTop: '0.5em' }}>
      <Header as="h3">
        <Icon name="zoom" />
        Language Detail: {language}
        {conventions && (
          <span style={{ fontWeight: 'normal', fontSize: '0.7em', marginLeft: '0.5em' }}>
            {conventions.language_name_english} ({conventions.language_name_native})
          </span>
        )}
      </Header>

      {conventions && (
        <div style={{ marginBottom: '1em' }}>
          <Header as="h4">Conventions</Header>
          <Table definition compact size="small">
            <Table.Body>
              <Table.Row>
                <Table.Cell width={4}>Script / Direction</Table.Cell>
                <Table.Cell>
                  {conventions.script || '-'} / {conventions.writing_direction || 'LTR'}
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Family</Table.Cell>
                <Table.Cell>{conventions.language_family || '-'}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Register</Table.Cell>
                <Table.Cell>{conventions.register || '-'}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Button Style</Table.Cell>
                <Table.Cell>{conventions.button_style || '-'}</Table.Cell>
              </Table.Row>
              {conventions.notes && (
                <Table.Row>
                  <Table.Cell>Notes</Table.Cell>
                  <Table.Cell>{conventions.notes}</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      )}

      {langStd.length > 0 && (
        <div style={{ marginBottom: '1em' }}>
          <Header as="h4">Standard Translations</Header>
          <Table compact celled size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Context</Table.HeaderCell>
                <Table.HeaderCell>Translation</Table.HeaderCell>
                <Table.HeaderCell>Form</Table.HeaderCell>
                <Table.HeaderCell>Source</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {langStd.map((st, i) => (
                <Table.Row key={i}>
                  <Table.Cell>
                    <Label size="tiny">{st.context}</Label>
                  </Table.Cell>
                  <Table.Cell>
                    <strong>{st.translation}</strong>
                  </Table.Cell>
                  <Table.Cell>{st.translation_form || '-'}</Table.Cell>
                  <Table.Cell>{st.source || '-'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {corrections.length > 0 && (
        <div style={{ marginBottom: '1em' }}>
          <Header as="h4">
            Correction History ({corrections.length})
          </Header>
          <Table compact celled size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Old Value</Table.HeaderCell>
                <Table.HeaderCell>New Value</Table.HeaderCell>
                <Table.HeaderCell>By</Table.HeaderCell>
                <Table.HeaderCell>When</Table.HeaderCell>
                <Table.HeaderCell>Notes</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {corrections.map((c, i) => (
                <Table.Row key={i}>
                  <Table.Cell>
                    <Label size="tiny">{c.correction_type}</Label>
                  </Table.Cell>
                  <Table.Cell style={{ color: '#c00', wordBreak: 'break-word' }}>
                    {c.old_value || '-'}
                  </Table.Cell>
                  <Table.Cell style={{ color: '#080', wordBreak: 'break-word' }}>
                    {c.new_value || '-'}
                  </Table.Cell>
                  <Table.Cell>{c.applied_by || '-'}</Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                    {c.applied_at
                      ? new Date(c.applied_at).toLocaleDateString()
                      : '-'}
                  </Table.Cell>
                  <Table.Cell>{c.notes || '-'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {verifications.length > 0 && (
        <div style={{ marginBottom: '1em' }}>
          <Header as="h4">
            Verification Events ({verifications.length})
          </Header>
          <Table compact celled size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Result</Table.HeaderCell>
                <Table.HeaderCell>Verifier</Table.HeaderCell>
                <Table.HeaderCell>Protocol</Table.HeaderCell>
                <Table.HeaderCell>Findings</Table.HeaderCell>
                <Table.HeaderCell>When</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {verifications.map((v, i) => (
                <Table.Row
                  key={i}
                  positive={v.result === 'pass'}
                  negative={v.result === 'fail'}
                >
                  <Table.Cell>
                    <Label size="tiny">{v.verification_type}</Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Label
                      size="tiny"
                      color={
                        v.result === 'pass'
                          ? 'green'
                          : v.result === 'fail'
                            ? 'red'
                            : 'grey'
                      }
                    >
                      {v.result}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>{v.verifier || '-'}</Table.Cell>
                  <Table.Cell>{v.protocol_reference || '-'}</Table.Cell>
                  <Table.Cell>{v.findings || '-'}</Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                    {v.verified_at
                      ? new Date(v.verified_at).toLocaleDateString()
                      : '-'}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {corrections.length === 0 && verifications.length === 0 && langStd.length === 0 && (
        <p style={{ color: '#999', fontStyle: 'italic' }}>
          No corrections, verifications, or standard translations recorded for this language.
        </p>
      )}
    </Segment>
  );
};

const CapaSection = ({ capas }) => {
  if (!capas || capas.length === 0) return null;
  return (
    <Segment>
      <Header as="h3">
        <Icon name="warning sign" />
        Related CAPAs ({capas.length})
      </Header>
      <Table compact celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>CAPA #</Table.HeaderCell>
            <Table.HeaderCell>Type</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Language</Table.HeaderCell>
            <Table.HeaderCell>Problem</Table.HeaderCell>
            <Table.HeaderCell>Opened</Table.HeaderCell>
            <Table.HeaderCell>Closed</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {capas.map((c, i) => (
            <Table.Row key={i}>
              <Table.Cell>
                <strong>{c.capa_number}</strong>
              </Table.Cell>
              <Table.Cell>
                <Label size="tiny">{c.capa_type}</Label>
              </Table.Cell>
              <Table.Cell>
                <Label
                  size="tiny"
                  color={
                    c.status === 'closed'
                      ? 'green'
                      : c.status === 'open'
                        ? 'red'
                        : 'orange'
                  }
                >
                  {c.status}
                </Label>
              </Table.Cell>
              <Table.Cell>{c.language || '-'}</Table.Cell>
              <Table.Cell style={{ wordBreak: 'break-word' }}>
                {c.problem_description || '-'}
              </Table.Cell>
              <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                {c.opened_at
                  ? new Date(c.opened_at).toLocaleDateString()
                  : '-'}
              </Table.Cell>
              <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                {c.closed_at
                  ? new Date(c.closed_at).toLocaleDateString()
                  : '-'}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Segment>
  );
};

// --- Sorting helper ---
function sortRows(rows, column, direction) {
  if (!column) return rows;
  const sorted = [...rows].sort((a, b) => {
    let va = a[column];
    let vb = b[column];
    // Numeric columns
    if (typeof va === 'number' && typeof vb === 'number') {
      return va - vb;
    }
    // Nulls sort last
    if (va == null) return 1;
    if (vb == null) return -1;
    va = String(va).toLowerCase();
    vb = String(vb).toLowerCase();
    return va < vb ? -1 : va > vb ? 1 : 0;
  });
  return direction === 'descending' ? sorted.reverse() : sorted;
}

// --- Filter options ---
const RISK_FILTER_OPTIONS = [
  { key: 'all', value: 'all', text: 'All risk levels' },
  { key: 'UNACCEPTABLE', value: 'UNACCEPTABLE', text: 'Unacceptable' },
  { key: 'ALARP', value: 'ALARP', text: 'ALARP' },
  { key: 'ACCEPTABLE', value: 'ACCEPTABLE', text: 'Acceptable' },
];

const STATUS_FILTER_OPTIONS = [
  { key: 'all', value: 'all', text: 'All statuses' },
  { key: 'missing', value: 'missing', text: 'Missing' },
  { key: 'translated', value: 'translated', text: 'Translated' },
  { key: 'fuzzy', value: 'fuzzy', text: 'Fuzzy' },
];

// --- Main Component ---
const MessageDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const initialLang = new URLSearchParams(location.search).get('language') || null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Grid state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('ascending');
  const [searchText, setSearchText] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Language focus state
  const [focusedLang, setFocusedLang] = useState(initialLang);
  const [langDetail, setLangDetail] = useState(null);
  const [langLoading, setLangLoading] = useState(false);

  // Collapsible sections
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    setLoading(true);
    fetchApi(`${getApiPath()}/@polyglot/messages/${id}`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  // Auto-fetch language detail when arriving with ?language= param
  useEffect(() => {
    if (initialLang && data && !langDetail) {
      setLangLoading(true);
      fetchApi(
        `${getApiPath()}/@polyglot/messages/${id}?language=${initialLang}`,
      )
        .then((json) => {
          setLangDetail(json.language_detail);
          setLangLoading(false);
        })
        .catch(() => setLangLoading(false));
    }
  }, [initialLang, data, id, langDetail]);

  const handleSort = useCallback(
    (col) => {
      if (sortColumn === col) {
        setSortDirection((d) =>
          d === 'ascending' ? 'descending' : 'ascending',
        );
      } else {
        setSortColumn(col);
        setSortDirection('ascending');
      }
    },
    [sortColumn],
  );

  const handleLangClick = useCallback(
    (lang) => {
      if (focusedLang === lang) {
        setFocusedLang(null);
        setLangDetail(null);
        return;
      }
      setFocusedLang(lang);
      setLangLoading(true);
      fetchApi(
        `${getApiPath()}/@polyglot/messages/${id}?language=${lang}`,
      )
        .then((json) => {
          setLangDetail(json.language_detail);
          setLangLoading(false);
        })
        .catch(() => {
          setLangLoading(false);
        });
    },
    [focusedLang, id],
  );

  const toggleSection = useCallback((key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Filtered + sorted translations
  const filteredTranslations = useMemo(() => {
    if (!data) return [];
    let rows = data.translations;
    const englishSource =
      data.message?.corrected_default || data.message?.msgid || '';

    // Text search
    if (searchText) {
      const q = searchText.toLowerCase();
      rows = rows.filter(
        (t) =>
          (t.language && t.language.toLowerCase().includes(q)) ||
          (t.language_name_native &&
            t.language_name_native.toLowerCase().includes(q)) ||
          (t.language_name_english &&
            t.language_name_english.toLowerCase().includes(q)) ||
          (t.msgstr && t.msgstr.toLowerCase().includes(q)),
      );
    }

    // Risk filter
    if (riskFilter !== 'all') {
      rows = rows.filter((t) => t.risk_level === riskFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      rows = rows.filter((t) => {
        const isMissing = !t.msgstr || t.msgstr === englishSource;
        if (statusFilter === 'missing') return isMissing;
        if (statusFilter === 'translated') return !isMissing;
        if (statusFilter === 'fuzzy') return t.fuzzy;
        return true;
      });
    }

    return sortRows(rows, sortColumn, sortDirection);
  }, [data, searchText, riskFilter, statusFilter, sortColumn, sortDirection]);

  if (loading) {
    return (
      <Container id="polyglot-message">
        <Loader active inline="centered" content="Loading message..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container id="polyglot-message">
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  const {
    message,
    correction_details,
    translations,
    preserved_terms,
    terminology,
    risk_classification,
    concept,
    disambiguations,
    source_evidence,
    cross_platform,
    standard_translations,
    capas,
    source_code,
    sibling_messages,
    qc_findings,
  } = data;

  if (!message) {
    return (
      <Container id="polyglot-message" style={{ paddingTop: '2em' }}>
        <Segment>Message #{id} not found.</Segment>
      </Container>
    );
  }

  const englishSource = message.corrected_default || message.msgid;
  const sourceRefs =
    message.source_refs && Array.isArray(message.source_refs)
      ? message.source_refs
      : [];

  // Column sort helper for header cells
  const sortableHeader = (label, col, width) => (
    <Table.HeaderCell
      width={width}
      sorted={sortColumn === col ? sortDirection : null}
      onClick={() => handleSort(col)}
      style={{ cursor: 'pointer' }}
    >
      {label}
    </Table.HeaderCell>
  );

  return (
    <Container
      id="polyglot-message"
      style={{ paddingTop: '1em', paddingBottom: '2em' }}
    >
      <Helmet title={`${englishSource} (#${id}) - Polyglot`} />
      <div style={{ marginBottom: '1em' }}>
        <Link to="/polyglot">&larr; Dashboard</Link>
        {' | '}
        <Link to={`/polyglot/browse/${message.platform}`}>
          {message.platform} translations
        </Link>
        {initialLang && (
          <>
            {' | '}
            <Link to={`/polyglot/qc/findings?language=${initialLang}`}>
              {initialLang} findings
            </Link>
          </>
        )}
      </div>

      <MessageHeader msg={message} risk={risk_classification} />

      <QcFindingsSection findings={qc_findings} messageId={id} />

      <ConceptSection
        concept={concept}
        disambiguations={disambiguations || []}
        sourceEvidence={source_evidence || []}
      />

      <EnglishSourceSection
        msg={message}
        correctionDetails={correction_details || []}
      />

      <SourceCodeSection sourceCode={source_code} siblingMessages={sibling_messages} sourceRefs={sourceRefs} />

      <CrossPlatformSection matches={cross_platform} />

      <RiskClassificationSection risk={risk_classification} />

      {/* --- Translation Grid --- */}
      <Segment>
        <Header as="h2" style={{ marginTop: 0 }}>
          <Icon name="world" />
          Translations
          <Header.Subheader>
            {translations.length} languages
            {filteredTranslations.length !== translations.length && (
              <span>
                {' '}
                (showing {filteredTranslations.length})
              </span>
            )}
          </Header.Subheader>
        </Header>

        <div
          style={{
            display: 'flex',
            gap: '0.8em',
            marginBottom: '1em',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Input
            icon="search"
            placeholder="Search languages or translations..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ minWidth: '250px' }}
          />
          <Dropdown
            selection
            compact
            options={RISK_FILTER_OPTIONS}
            value={riskFilter}
            onChange={(e, { value }) => setRiskFilter(value)}
          />
          <Dropdown
            selection
            compact
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(e, { value }) => setStatusFilter(value)}
          />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <Table compact celled sortable selectable>
            <Table.Header>
              <Table.Row>
                {sortableHeader('Code', 'language', 1)}
                {sortableHeader('Language', 'language_name_english', 2)}
                {sortableHeader('Translation', 'msgstr', 6)}
                {sortableHeader('Source', 'translation_source', 1)}
                {sortableHeader('Status', 'review_state', 1)}
                {sortableHeader('Risk', 'rpn', 1)}
                {sortableHeader('Corr.', 'correction_count', 1)}
                {sortableHeader('Verif.', 'verification_count', 1)}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredTranslations.map((t) => {
                const isMissing = !t.msgstr || t.msgstr === englishSource;
                const isActive = focusedLang === t.language;
                return (
                  <React.Fragment key={t.language}>
                    <Table.Row
                      warning={isMissing}
                      active={isActive}
                      style={
                        !isMissing && t.risk_level
                          ? riskRowStyle(t.risk_level)
                          : undefined
                      }
                      onClick={() => handleLangClick(t.language)}
                      className="cursor-pointer"
                    >
                      <Table.Cell>
                        <strong>{t.language}</strong>
                        {t.writing_direction === 'RTL' && (
                          <Icon
                            name="text width"
                            size="small"
                            style={{ marginLeft: '0.3em' }}
                            title="RTL"
                          />
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {t.language_name_english || '-'}
                        {t.language_name_native &&
                          t.language_name_native !== t.language_name_english && (
                            <div
                              style={{
                                fontSize: '0.85em',
                                color: '#666',
                              }}
                            >
                              {t.language_name_native}
                            </div>
                          )}
                      </Table.Cell>
                      <Table.Cell style={{ wordBreak: 'break-word' }}>
                        {isMissing ? (
                          <em style={{ color: '#999' }}>
                            {t.msgstr || '(empty)'}
                          </em>
                        ) : (
                          <span dir={t.writing_direction === 'RTL' ? 'rtl' : 'ltr'}>
                            {t.msgstr}
                          </span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Label size="tiny" basic>
                          {t.translation_source || '-'}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        {t.review_state && (
                          <Label
                            size="tiny"
                            color={
                              t.review_state === 'approved'
                                ? 'green'
                                : t.review_state === 'rejected'
                                  ? 'red'
                                  : null
                            }
                          >
                            {t.review_state}
                          </Label>
                        )}
                        {t.fuzzy && (
                          <Label size="tiny" color="yellow">
                            fuzzy
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {t.severity_code ? (
                          <Label
                            size="tiny"
                            color={riskColor(t.severity_code)}
                            title={`RPN: ${t.rpn || '-'} (${t.severity_code}×${t.probability_code}×${t.detectability_code})`}
                          >
                            {t.risk_level || '-'}
                            {t.rpn != null && (
                              <Label.Detail>{t.rpn}</Label.Detail>
                            )}
                          </Label>
                        ) : (
                          <span style={{ color: '#ccc' }}>-</span>
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {parseInt(t.correction_count, 10) > 0 ? (
                          <Label size="tiny" circular>
                            {t.correction_count}
                          </Label>
                        ) : (
                          <span style={{ color: '#ccc' }}>0</span>
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {parseInt(t.verification_count, 10) > 0 ? (
                          <Label size="tiny" circular color="blue">
                            {t.verification_count}
                          </Label>
                        ) : (
                          <span style={{ color: '#ccc' }}>0</span>
                        )}
                      </Table.Cell>
                    </Table.Row>
                    {isActive && (
                      <Table.Row>
                        <Table.Cell colSpan="8" style={{ padding: 0 }}>
                          {langLoading ? (
                            <Segment basic>
                              <Loader
                                active
                                inline="centered"
                                content={`Loading ${t.language} details...`}
                              />
                            </Segment>
                          ) : (
                            <LanguageDetailPanel
                              detail={langDetail}
                              standardTranslations={standard_translations || []}
                            />
                          )}
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      </Segment>

      {/* --- Collapsible: Preserved Terms --- */}
      {preserved_terms && preserved_terms.length > 0 && (
        <Segment>
          <Accordion>
            <Accordion.Title
              active={openSections.preserved}
              onClick={() => toggleSection('preserved')}
            >
              <Header as="h3" style={{ display: 'inline' }}>
                <Icon name="dropdown" />
                Preserved Terms ({preserved_terms.length})
              </Header>
            </Accordion.Title>
            <Accordion.Content active={openSections.preserved}>
              <Table compact celled size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Language</Table.HeaderCell>
                    <Table.HeaderCell>Term</Table.HeaderCell>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Context</Table.HeaderCell>
                    <Table.HeaderCell>Notes</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {preserved_terms.map((pt, i) => (
                    <Table.Row key={i}>
                      <Table.Cell>{pt.language_code}</Table.Cell>
                      <Table.Cell>{pt.term}</Table.Cell>
                      <Table.Cell>{pt.term_type}</Table.Cell>
                      <Table.Cell>{pt.context}</Table.Cell>
                      <Table.Cell>{pt.notes}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Accordion.Content>
          </Accordion>
        </Segment>
      )}

      {/* --- Collapsible: Terminology References --- */}
      {terminology && terminology.length > 0 && (
        <Segment>
          <Accordion>
            <Accordion.Title
              active={openSections.terminology}
              onClick={() => toggleSection('terminology')}
            >
              <Header as="h3" style={{ display: 'inline' }}>
                <Icon name="dropdown" />
                Terminology References ({terminology.length})
              </Header>
            </Accordion.Title>
            <Accordion.Content active={openSections.terminology}>
              <Table compact celled size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Language</Table.HeaderCell>
                    <Table.HeaderCell>Term</Table.HeaderCell>
                    <Table.HeaderCell>Translation</Table.HeaderCell>
                    <Table.HeaderCell>Source</Table.HeaderCell>
                    <Table.HeaderCell>Reliability</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {terminology.map((t, i) => (
                    <Table.Row key={i}>
                      <Table.Cell>{t.language_code}</Table.Cell>
                      <Table.Cell>{t.english_term}</Table.Cell>
                      <Table.Cell>{t.translation}</Table.Cell>
                      <Table.Cell>{t.source}</Table.Cell>
                      <Table.Cell>{t.reliability}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Accordion.Content>
          </Accordion>
        </Segment>
      )}

      {/* --- Collapsible: Standard Translations --- */}
      {standard_translations && standard_translations.length > 0 && (
        <Segment>
          <Accordion>
            <Accordion.Title
              active={openSections.standard}
              onClick={() => toggleSection('standard')}
            >
              <Header as="h3" style={{ display: 'inline' }}>
                <Icon name="dropdown" />
                Standard Translations ({standard_translations.length})
              </Header>
            </Accordion.Title>
            <Accordion.Content active={openSections.standard}>
              <Table compact celled size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Language</Table.HeaderCell>
                    <Table.HeaderCell>Context</Table.HeaderCell>
                    <Table.HeaderCell>Translation</Table.HeaderCell>
                    <Table.HeaderCell>Form</Table.HeaderCell>
                    <Table.HeaderCell>Gender</Table.HeaderCell>
                    <Table.HeaderCell>Source</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {standard_translations.map((st, i) => (
                    <Table.Row key={i}>
                      <Table.Cell>{st.language_code}</Table.Cell>
                      <Table.Cell>
                        <Label size="tiny">{st.context}</Label>
                      </Table.Cell>
                      <Table.Cell>
                        <strong>{st.translation}</strong>
                      </Table.Cell>
                      <Table.Cell>{st.translation_form || '-'}</Table.Cell>
                      <Table.Cell>{st.grammatical_gender || '-'}</Table.Cell>
                      <Table.Cell>{st.source || '-'}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Accordion.Content>
          </Accordion>
        </Segment>
      )}

      <CapaSection capas={capas} />
    </Container>
  );
};

// --- QC Findings for this message ---
const FINDING_STATUS_COLOR = {
  triaged: 'orange',
  fixed_local: 'green',
  reported: 'blue',
  wontfix: 'grey',
  open: 'red',
  fixed_upstream: 'teal',
};

const QcFindingsSection = ({ findings, messageId }) => {
  if (!findings || findings.length === 0) return null;

  const pending = findings.filter(
    (f) => !['fixed_local', 'fixed_upstream', 'wontfix'].includes(f.status),
  );
  const resolved = findings.filter((f) =>
    ['fixed_local', 'fixed_upstream', 'wontfix'].includes(f.status),
  );

  return (
    <Segment>
      <Header as="h3">
        <Icon name="clipboard check" />
        QC Findings ({findings.length})
        {pending.length > 0 && (
          <Label color="orange" size="small" style={{ marginLeft: '0.5em' }}>
            {pending.length} pending
          </Label>
        )}
      </Header>
      <Table compact celled size="small">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Lang</Table.HeaderCell>
            <Table.HeaderCell>Error Type</Table.HeaderCell>
            <Table.HeaderCell>Finding</Table.HeaderCell>
            <Table.HeaderCell>Suggested Fix</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {findings.map((f) => (
            <Table.Row
              key={f.id}
              positive={f.status === 'fixed_local'}
              warning={f.status === 'triaged'}
            >
              <Table.Cell><strong>{f.language}</strong></Table.Cell>
              <Table.Cell>{f.error_type_label || f.error_type}</Table.Cell>
              <Table.Cell style={{ maxWidth: '350px', wordBreak: 'break-word' }}>
                {f.finding}
              </Table.Cell>
              <Table.Cell style={{ maxWidth: '200px', wordBreak: 'break-word' }}>
                {f.suggested_fix ? (
                  <span style={{ color: '#16ab39' }}>{f.suggested_fix}</span>
                ) : (
                  <span style={{ color: '#ccc' }}>&mdash;</span>
                )}
              </Table.Cell>
              <Table.Cell>
                <Label
                  size="mini"
                  color={FINDING_STATUS_COLOR[f.status] || 'grey'}
                >
                  {f.status_label || f.status}
                </Label>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <div style={{ marginTop: '0.5em' }}>
        <Link to={`/polyglot/qc/findings?message_id=${messageId}`}>
          View in QC Findings Browser &rarr;
        </Link>
      </div>
    </Segment>
  );
};

export default MessageDetail;
